import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, BookOpen, Clock, Layers, Check, Loader2, Eye, Mail, Gift } from "lucide-react";

const BookDetail = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // Free-book email-capture state (logged-out branch)
  const [claimEmail, setClaimEmail] = useState("");
  const [claimName, setClaimName] = useState("");
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await axios.get(`${API}/books/${bookId}`);
        setBook(response.data);
      } catch (error) {
        console.error("Failed to fetch book", error);
        toast.error("Book not found");
        navigate("/library");
      } finally {
        setLoading(false);
      }
    };
    fetchBook();
  }, [bookId, navigate]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.info("Please sign in to add items to cart");
      navigate("/auth");
      return;
    }

    setAdding(true);
    try {
      await axios.post(
        `${API}/cart/add`,
        { book_id: book.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${book.title} added to cart`);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to add to cart";
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const handleClaimLoggedIn = async () => {
    setAdding(true);
    try {
      const res = await axios.post(
        `${API}/books/${book.id}/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.already_owned) {
        toast.info("Already in your library");
      } else {
        toast.success("Added to your library");
      }
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Couldn't add to library";
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const handleClaimByEmail = async (e) => {
    e.preventDefault();
    if (!claimEmail) {
      toast.error("Please enter your email");
      return;
    }
    setClaimSubmitting(true);
    try {
      await axios.post(`${API}/books/${book.id}/claim-by-email`, {
        email: claimEmail,
        name: claimName || null,
      });
      setClaimSubmitted(true);
      toast.success("Check your email — your free copy is on its way.");
    } catch (error) {
      const message = error.response?.data?.detail || "Couldn't send your free copy";
      toast.error(message);
    } finally {
      setClaimSubmitting(false);
    }
  };

  const levelColors = {
    entry: "bg-green-500/20 text-green-400 border-green-500/30",
    intermediate: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    advanced: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    practitioner: "bg-gold/20 text-gold border-gold/30",
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (!book) return null;

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="book-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Back Link */}
        <Link 
          to="/library" 
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-gold mb-8 group"
          data-testid="back-to-library"
        >
          <ArrowLeft size={16} className="transform group-hover:-translate-x-1 transition-transform duration-300" />
          <span className="text-sm uppercase tracking-widest">Back to Library</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[3/4] overflow-hidden rounded-sm border border-white/10">
              <img
                src={book.cover_image}
                alt={book.title}
                className="w-full h-full object-cover"
                data-testid="book-cover-image"
              />
            </div>
            <div className="glow-orb w-96 h-96 -bottom-20 -left-20 opacity-30" />
          </div>

          {/* Content */}
          <div>
            {/* Level Badge */}
            <span className={`inline-block px-3 py-1 text-xs uppercase tracking-wider rounded-sm border ${levelColors[book.reading_level]} mb-4`}>
              {book.reading_level}
            </span>

            {/* Title */}
            <h1 className="font-heading text-4xl md:text-5xl text-[#EAEAEA] mb-2" data-testid="book-title">
              {book.title}
            </h1>
            
            {book.subtitle && (
              <p className="font-heading text-xl text-[#A1A1AA] italic mb-6" data-testid="book-subtitle">
                {book.subtitle}
              </p>
            )}

            {/* Meta */}
            <div className="flex flex-wrap gap-6 mb-8 py-6 border-y border-white/5">
              {book.page_count && (
                <div className="flex items-center gap-2 text-[#A1A1AA]">
                  <BookOpen size={18} />
                  <span className="text-sm">{book.page_count} pages</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Layers size={18} />
                <span className="text-sm">{book.category}</span>
              </div>
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Clock size={18} />
                <span className="text-sm">Book {book.reading_order} of 7</span>
              </div>
            </div>

            {/* Description */}
            <div className="prose prose-invert max-w-none mb-8">
              <p className="text-[#A1A1AA] leading-relaxed" data-testid="book-description">
                {book.description}
              </p>
            </div>

            {/* Preview Chapters */}
            {book.preview_chapters && book.preview_chapters.length > 0 && (
              <div className="mb-8">
                <h3 className="font-heading text-lg text-[#EAEAEA] mb-3">Preview includes:</h3>
                <ul className="space-y-2">
                  {book.preview_chapters.map((chapter, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                      <Check size={14} className="text-gold" />
                      {chapter}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Price & CTA — branches for free vs paid books */}
            {book.price === 0 ? (
              <div className="p-6 bg-paper border border-gold/30 rounded-sm" data-testid="free-book-cta">
                <div className="flex items-center gap-3 mb-4">
                  <Gift size={20} className="text-gold" />
                  <span className="font-mono text-2xl text-gold uppercase tracking-widest">Free</span>
                  <span className="text-[#A1A1AA] text-sm">— your entry point into Somalume</span>
                </div>

                {user ? (
                  // Logged in — one-click claim
                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <a
                      href={`/api/books/${book.id}/free-read`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center h-14 px-6 border border-gold/40 text-gold hover:bg-gold/10 rounded-none uppercase text-xs tracking-widest transition-colors"
                      data-testid="read-preview-btn"
                    >
                      <Eye size={16} className="mr-2" />
                      Read Now
                    </a>
                    <Button
                      onClick={handleClaimLoggedIn}
                      disabled={adding}
                      className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest btn-shine w-full sm:w-auto"
                      data-testid="claim-free-btn"
                    >
                      {adding ? <Loader2 size={16} className="animate-spin" /> : (
                        <>
                          <BookOpen size={16} className="mr-2" />
                          Add to your library
                        </>
                      )}
                    </Button>
                  </div>
                ) : claimSubmitted ? (
                  // Logged out — success state after email submission
                  <div className="mt-6 p-4 border border-gold/20 rounded-sm bg-gold/5" data-testid="free-claim-success">
                    <p className="text-[#EAEAEA] mb-2">
                      <Check className="inline mr-2 text-gold" size={16} />
                      Sent. Check your inbox in the next minute or two.
                    </p>
                    <p className="text-[#A1A1AA] text-sm">
                      Your reader link is in the email. To save Eight Pillars to a permanent
                      library and unlock the rest of the catalogue,{" "}
                      <Link to="/auth" className="text-gold underline">create an account</Link>{" "}
                      with the same email — it'll be waiting for you.
                    </p>
                  </div>
                ) : (
                  // Logged out — email capture form
                  <form onSubmit={handleClaimByEmail} className="mt-6 space-y-3" data-testid="free-claim-form">
                    <p className="text-[#A1A1AA] text-sm mb-3">
                      Enter your email — I'll send you the reader and a quiet
                      occasional note from Ben.
                    </p>
                    <input
                      type="text"
                      placeholder="Your name (optional)"
                      value={claimName}
                      onChange={(e) => setClaimName(e.target.value)}
                      className="w-full bg-[#050505] border border-white/10 focus:border-gold/40 text-[#EAEAEA] rounded-none h-12 px-4 text-sm outline-none"
                      data-testid="free-claim-name"
                    />
                    <input
                      type="email"
                      required
                      placeholder="your@email.com"
                      value={claimEmail}
                      onChange={(e) => setClaimEmail(e.target.value)}
                      className="w-full bg-[#050505] border border-white/10 focus:border-gold/40 text-[#EAEAEA] rounded-none h-12 px-4 text-sm outline-none"
                      data-testid="free-claim-email"
                    />
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={`/api/books/${book.id}/free-read`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center h-14 px-6 border border-gold/40 text-gold hover:bg-gold/10 rounded-none uppercase text-xs tracking-widest transition-colors"
                        data-testid="read-now-btn"
                      >
                        <Eye size={16} className="mr-2" />
                        Read Now
                      </a>
                      <Button
                        type="submit"
                        disabled={claimSubmitting}
                        className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest btn-shine w-full sm:w-auto"
                        data-testid="free-claim-submit"
                      >
                        {claimSubmitting ? <Loader2 size={16} className="animate-spin" /> : (
                          <>
                            <Mail size={16} className="mr-2" />
                            Get free — Email me
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-[#777] text-xs">
                      Already have an account?{" "}
                      <Link to="/auth" className="text-gold underline">Sign in</Link>{" "}
                      and add it instantly.
                    </p>
                  </form>
                )}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-6 bg-paper border border-white/5 rounded-sm">
                <div>
                  <span className="text-[#A1A1AA] text-sm block mb-1">Price</span>
                  <span className="font-mono text-3xl text-gold" data-testid="book-price">
                    ${book.price.toFixed(2)} AUD
                  </span>
                </div>
                <div className="flex-1" />
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <a
                    href={`/api/books/${book.id}/preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-14 px-6 border border-gold/40 text-gold hover:bg-gold/10 rounded-none uppercase text-xs tracking-widest transition-colors"
                    data-testid="read-preview-btn"
                  >
                    <Eye size={16} className="mr-2" />
                    Read Preview
                  </a>
                  <Button
                    onClick={handleAddToCart}
                    disabled={adding}
                    className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest btn-shine w-full sm:w-auto"
                    data-testid="add-to-cart-btn"
                  >
                    {adding ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        <ShoppingCart size={16} className="mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookDetail;
